import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Building2,
  MessagesSquare,
  Crown,
  FileText,
  ClipboardList,
  BarChart3,
  Users,
  Globe,
  Bell,
  Settings,
  Search,
  KeyRound,
  LogOut,
  Menu,
  X,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { platformAPI, proxyAPI } from '../services/api'
import { useTenantDirectory } from '../lib/tenantDirectory'
import { toTenantModel, tenantView } from '../lib/support'
import { BrandMark } from './BrandMark'
import { CommandPalette } from './CommandPalette'
import { Dot } from './ui'

// Support Console shell: 232px sidebar | content, sticky header with the ⌘K search
// trigger and platform health. ownerOnly entries are hidden from staff (the API refuses
// them anyway).
const SUPPORT_NAV = [
  { name: 'Hoje', href: '/', icon: Home, badge: true },
  { name: 'Estúdios', href: '/tenants', icon: Building2 },
  { name: 'Conversas de suporte', href: '/support-analysis', icon: MessagesSquare },
]

const PLATFORM_NAV = [
  { name: 'Planos de assinatura', href: '/subscription-tiers', icon: Crown },
  { name: 'Políticas', href: '/policies', icon: FileText },
  { name: 'Formulários públicos', href: '/public-forms', icon: ClipboardList, ownerOnly: true },
  { name: 'Análises', href: '/analytics', icon: BarChart3, ownerOnly: true },
  { name: 'Usuários admin', href: '/users', icon: Users, ownerOnly: true },
  { name: 'Proxies', href: '/system', icon: Globe },
  { name: 'Notificações', href: '/notifications', icon: Bell },
  { name: 'Configurações', href: '/settings', icon: Settings },
]

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

const usePlatformHealth = (enabled) => {
  const [health, setHealth] = useState(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const load = async () => {
      const [h, p] = await Promise.allSettled([platformAPI.getSystemHealth(), proxyAPI.getAll()])
      if (cancelled) return
      const proxies = p.status === 'fulfilled' ? p.value.data?.data || [] : null
      setHealth({
        api: h.status === 'fulfilled' ? h.value.data?.status || 'healthy' : 'unreachable',
        proxies: proxies ? { valid: proxies.filter((x) => x.valid).length, total: proxies.length } : null,
      })
    }
    load()
    const id = setInterval(load, 60_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [enabled])
  return health
}

const HealthSummary = ({ health }) => {
  if (!health) return null
  const apiTone = health.api === 'healthy' ? 'ok' : health.api === 'degraded' ? 'warn' : 'crit'
  const apiLabel = { healthy: 'API saudável', degraded: 'API degradada', unhealthy: 'API com problemas' }[health.api] || 'API inacessível'
  const p = health.proxies
  const proxyTone = !p || p.total === 0 ? 'muted' : p.valid === p.total ? 'ok' : p.valid === 0 ? 'crit' : 'warn'
  return (
    <div className="ml-auto hidden items-center gap-2 whitespace-nowrap text-xs text-admin-600 md:flex">
      <Dot tone={apiTone} />
      {apiLabel}
      {p && p.total === 0 && (
        <>
          <Dot tone="muted" className="ml-2" />
          Nenhum proxy sincronizado
        </>
      )}
      {p && p.total > 0 && (
        <>
          <Dot tone={proxyTone} className="ml-2" />
          {p.valid} de {p.total} proxies válidos
        </>
      )}
    </div>
  )
}

const Sidebar = ({ navigation, platform, attentionCount, isActive, user, isOwner, onLogout, onNavigate }) => (
  <aside className="flex h-full flex-col gap-4 overflow-y-auto bg-admin-800 px-3 py-4 text-admin-200">
    <BrandMark />
    {/* Not a <nav>: keep landmark semantics without competing with page-level navs. */}
    <div role="navigation" aria-label="Principal" className="flex flex-col gap-0.5">
      {navigation.length > 0 && <span className="micro-label px-2.5 py-1.5 text-admin-400">Suporte</span>}
      {navigation.map((item) => {
        const Icon = item.icon
        const active = isActive(item.href)
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
              active ? 'bg-admin-900 text-white' : 'text-admin-300 hover:bg-admin-700 hover:text-white'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{item.name}</span>
            {item.badge && attentionCount > 0 && (
              <span
                className="min-w-[22px] rounded-[10px] bg-red-600 px-[7px] py-px text-center text-[11px] font-semibold text-white"
                aria-label={`${attentionCount} estúdios precisam de atenção`}
              >
                {attentionCount}
              </span>
            )}
          </Link>
        )
      })}
      {platform.length > 0 && <span className="micro-label px-2.5 pb-1.5 pt-3.5 text-admin-400">Plataforma</span>}
      {platform.map((item) => {
        const Icon = item.icon
        const active = isActive(item.href)
        return (
          <Link
            key={item.href}
            to={item.href}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-2.5 rounded-md px-3 py-[7px] text-[13px] transition-colors ${
              active ? 'bg-admin-900 text-white' : 'text-admin-400 hover:bg-admin-700 hover:text-white'
            }`}
          >
            <Icon className="h-[15px] w-[15px]" />
            {item.name}
          </Link>
        )
      })}
    </div>

    <div className="mt-auto flex flex-col gap-2 rounded-md bg-admin-900 p-2.5">
      <div className="flex items-center gap-2.5">
        <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-primary-500 text-[13px] font-bold text-white">
          {user?.email?.[0]?.toUpperCase() || 'A'}
        </span>
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-[13px] font-semibold text-white" title={user?.email}>
            {user?.email || 'Admin da plataforma'}
          </span>
          <span className="text-[11px] text-admin-400">{isOwner ? 'Proprietário' : 'Equipe'} · Admin da plataforma</span>
        </div>
      </div>
      <div className="flex gap-1">
        {!user?.is_builtin && (
          <Link
            to="/account/password"
            onClick={onNavigate}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-admin-300 hover:bg-admin-700 hover:text-white"
          >
            <KeyRound className="h-3.5 w-3.5" />
            Alterar senha
          </Link>
        )}
        <button
          type="button"
          onClick={onLogout}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-admin-300 hover:bg-admin-700 hover:text-white"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sair
        </button>
      </div>
    </div>
  </aside>
)

export const Layout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const { user, logout, isOwner, mustChangePassword } = useAuth()
  const location = useLocation()
  const enabled = !mustChangePassword
  const health = usePlatformHealth(enabled)
  const { tenants } = useTenantDirectory()

  const attentionCount = useMemo(
    () => (enabled ? tenants.filter((t) => tenantView(toTenantModel(t)).needsAttention).length : 0),
    [tenants, enabled]
  )

  const isActive = (href) => (href === '/' ? location.pathname === '/' : location.pathname.startsWith(href))

  const navigation = enabled ? SUPPORT_NAV : []
  const platform = enabled ? PLATFORM_NAV.filter((item) => isOwner || !item.ownerOnly) : []

  useEffect(() => {
    if (!enabled) return
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])

  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  const sidebarProps = { navigation, platform, attentionCount, isActive, user, isOwner, onLogout: handleLogout }

  return (
    <div className="min-h-screen bg-admin-50 lg:grid lg:grid-cols-[232px_minmax(0,1fr)]">
      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-[rgba(15,23,42,.45)]" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[260px]">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-3 z-10 rounded-md p-1.5 text-admin-300 hover:text-white"
              aria-label="Fechar menu"
            >
              <X className="h-5 w-5" />
            </button>
            <Sidebar {...sidebarProps} onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      <div className="sticky top-0 hidden h-screen lg:block">
        <Sidebar {...sidebarProps} />
      </div>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-admin-200 bg-white px-4 py-3 sm:px-6">
          <button
            type="button"
            className="-ml-1 rounded-md p-1.5 text-admin-700 lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          {enabled && (
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex min-w-0 max-w-[620px] flex-1 cursor-text items-center gap-2.5 rounded-md border border-admin-300 bg-white px-4 py-[9px] text-left text-sm text-admin-500 hover:border-admin-400"
            >
              <Search className="h-4 w-4 flex-none" />
              <span className="min-w-0 flex-1 truncate">Buscar estúdios, pessoas, clientes, e-mails, telefones, conversas…</span>
              <span className="hidden rounded-md border border-admin-200 bg-admin-50 px-2 py-0.5 text-[11px] sm:inline">
                {isMac ? '⌘K' : 'Ctrl K'}
              </span>
            </button>
          )}
          <HealthSummary health={health} />
        </header>

        <main className="flex flex-col gap-6 px-4 pb-8 pt-6 sm:px-6">{children}</main>
      </div>

      {enabled && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
    </div>
  )
}
